import {
  BTN_DOWN,
  BTN_FIRE,
  BTN_LEFT,
  BTN_RELOAD,
  BTN_RIGHT,
  BTN_WALK,
  BTN_UP,
  FLAG_SCREEN_RELATIVE,
  BTN_SWITCH,
} from './input';
import {
  BACKPEDAL_SPEED_FACTOR,
  CAMO_REVEAL_TICKS,
  CLIP_SIZE,
  MAX_HEALTH,
  MAX_PICKUPS,
  MAX_PLAYERS,
  MAX_PROJECTILES,
  PLAYER_RADIUS,
  RELOAD_TICKS,
  RESERVE_START,
  RIFLE_COOLDOWN_TICKS,
  RUN_SPEED,
  SPAWN_PROTECT_TICKS,
  SPREAD_RUN,
  SPREAD_STAND,
  SPREAD_WALK,
  WALK_SPEED,
  WEAPON_RIFLE,
  WEAPON_GRENADE,
  WEAPON_SWITCH_TICKS,
  GRENADE_START,
  GRENADE_COOLDOWN_TICKS,
  GRENADE_RANGE_MIN,
  GRENADE_RANGE_MAX,
} from './constants';
import { isSolid, type LevelData } from './map';
import { spawnProjectile, spawnGrenade } from './combat';

// Mutable per-tick outputs and mode switches. Numbers, not objects, so the
// tick path still allocates nothing.
export interface WorldOut {
  // false on the client: bullets vanish on contact and the contact is
  // recorded (hitShooter/hitVictim, for the hit flash) but health never
  // changes and no kill is recorded (the server's snapshots carry the truth).
  authoritative: boolean;
  // true while the client replays pending inputs after a reconcile, so the
  // replay doesn't spawn duplicate bullets.
  suppressSpawns: boolean;
  hitCount: number;
  killCount: number;
  boomCount: number;
}

// World state is struct-of-arrays in preallocated typed arrays, indexed by
// player slot (or projectile slot). No per-entity objects: the tick loop
// must not allocate, and this layout ports to C++ almost line for line.
export interface World {
  readonly level: LevelData;
  readonly max: number;
  readonly alive: Uint8Array; // slot in use (connected)
  readonly team: Uint8Array; // NO_TEAM in deathmatch
  // Rules the room sets; read by the combat step.
  friendlyFire: boolean;
  readonly x: Float32Array;
  readonly y: Float32Array;
  readonly aim: Float32Array; // radians
  readonly buttons: Uint16Array; // last applied buttons
  readonly lastSeq: Uint32Array; // last input sequence applied
  // combat
  readonly health: Uint8Array; // 0 = dead (awaiting respawn)
  readonly ammo: Uint8Array; // rounds in the clip
  readonly reserve: Uint8Array;
  readonly cooldown: Uint8Array; // ticks until the rifle can fire again
  readonly reload: Uint8Array; // ticks of reload remaining, 0 = not reloading
  readonly invuln: Uint8Array; // spawn-protection ticks remaining
  readonly fireHeld: Uint8Array; // FIRE was down last tick (for press edges)
  // transient output of applyInput: 1 if a round was spent this tick
  readonly shotFired: Uint8Array;
  // weapons: WEAPON_RIFLE | WEAPON_GRENADE, the switch countdown, grenades
  // carried, throw cooldown, SWITCH held last tick (press edge), and 1 if a
  // grenade left the hand this tick (transient, like shotFired)
  readonly weapon: Uint8Array;
  readonly switchTicks: Uint8Array;
  readonly grenades: Uint8Array;
  readonly throwCooldown: Uint8Array;
  readonly switchHeld: Uint8Array;
  readonly grenadeThrown: Uint8Array;
  // camouflage
  readonly camoTicks: Uint16Array; // charge remaining, 0 = none
  readonly camoProp: Uint8Array; // chosen prop (PROP_*), 0 = not chosen yet
  readonly revealTicks: Uint8Array; // after shooting, forced visible this long
  readonly disguised: Uint8Array; // prop currently shown to others, or 0

  // projectile pool
  readonly pAlive: Uint8Array;
  readonly pX: Float32Array;
  readonly pY: Float32Array;
  readonly pVX: Float32Array;
  readonly pVY: Float32Array;
  readonly pOwner: Uint8Array;
  readonly pDist: Float32Array; // distance travelled
  readonly pKind: Uint8Array; // PROJ_BULLET | PROJ_GRENADE
  readonly pTtl: Uint16Array; // grenade: ticks until it bursts
  readonly pRange: Float32Array; // grenade: throw distance (arc height for drawing)
  // explosions this tick (both ends: the client draws them)
  readonly boomX: Float32Array;
  readonly boomY: Float32Array;
  readonly boomOwner: Uint8Array;

  // per-tick combat output (server reads after stepProjectiles)
  readonly hitShooter: Uint8Array;
  readonly hitVictim: Uint8Array;
  readonly hitDamage: Uint8Array;
  readonly killVictim: Uint8Array;
  readonly killShooter: Uint8Array;

  // pickup pool (health kits, ammo crates)
  readonly kAlive: Uint8Array;
  readonly kKind: Uint8Array;
  readonly kX: Float32Array;
  readonly kY: Float32Array;
  readonly kAmount: Uint8Array;
  readonly kTtl: Uint16Array; // ticks until it despawns
  readonly kVX: Float32Array; // sliding out after a death (server only)
  readonly kVY: Float32Array;
  readonly kShield: Uint8Array; // ticks during which bullets pass over it

  readonly out: WorldOut;
}

export function createWorld(level: LevelData, max: number = MAX_PLAYERS): World {
  return {
    level,
    max,
    alive: new Uint8Array(max),
    team: new Uint8Array(max),
    friendlyFire: true,
    x: new Float32Array(max),
    y: new Float32Array(max),
    aim: new Float32Array(max),
    buttons: new Uint16Array(max),
    lastSeq: new Uint32Array(max),
    health: new Uint8Array(max),
    ammo: new Uint8Array(max),
    reserve: new Uint8Array(max),
    cooldown: new Uint8Array(max),
    reload: new Uint8Array(max),
    invuln: new Uint8Array(max),
    fireHeld: new Uint8Array(max),
    shotFired: new Uint8Array(max),
    weapon: new Uint8Array(max),
    switchTicks: new Uint8Array(max),
    grenades: new Uint8Array(max),
    throwCooldown: new Uint8Array(max),
    switchHeld: new Uint8Array(max),
    grenadeThrown: new Uint8Array(max),
    camoTicks: new Uint16Array(max),
    camoProp: new Uint8Array(max),
    revealTicks: new Uint8Array(max),
    disguised: new Uint8Array(max),
    pAlive: new Uint8Array(MAX_PROJECTILES),
    pX: new Float32Array(MAX_PROJECTILES),
    pY: new Float32Array(MAX_PROJECTILES),
    pVX: new Float32Array(MAX_PROJECTILES),
    pVY: new Float32Array(MAX_PROJECTILES),
    pOwner: new Uint8Array(MAX_PROJECTILES),
    pDist: new Float32Array(MAX_PROJECTILES),
    pKind: new Uint8Array(MAX_PROJECTILES),
    pTtl: new Uint16Array(MAX_PROJECTILES),
    pRange: new Float32Array(MAX_PROJECTILES),
    boomX: new Float32Array(MAX_PROJECTILES),
    boomY: new Float32Array(MAX_PROJECTILES),
    boomOwner: new Uint8Array(MAX_PROJECTILES),
    hitShooter: new Uint8Array(MAX_PROJECTILES),
    hitVictim: new Uint8Array(MAX_PROJECTILES),
    hitDamage: new Uint8Array(MAX_PROJECTILES),
    killVictim: new Uint8Array(MAX_PROJECTILES),
    killShooter: new Uint8Array(MAX_PROJECTILES),
    kAlive: new Uint8Array(MAX_PICKUPS),
    kKind: new Uint8Array(MAX_PICKUPS),
    kX: new Float32Array(MAX_PICKUPS),
    kY: new Float32Array(MAX_PICKUPS),
    kAmount: new Uint8Array(MAX_PICKUPS),
    kTtl: new Uint16Array(MAX_PICKUPS),
    kVX: new Float32Array(MAX_PICKUPS),
    kVY: new Float32Array(MAX_PICKUPS),
    kShield: new Uint8Array(MAX_PICKUPS),
    out: { authoritative: true, suppressSpawns: false, hitCount: 0, killCount: 0, boomCount: 0 },
  };
}

// Returns the slot index, or -1 if the world is full.
export function spawnPlayer(w: World, x: number, y: number): number {
  for (let i = 0; i < w.max; i++) {
    if (w.alive[i] === 0) {
      w.alive[i] = 1;
      w.buttons[i] = 0;
      w.lastSeq[i] = 0;
      w.fireHeld[i] = 0;
      respawnPlayer(w, i, x, y);
      return i;
    }
  }
  return -1;
}

// Put a (new or dead) player back on the field with full health and ammo
// and a few seconds of spawn protection.
export function respawnPlayer(w: World, id: number, x: number, y: number): void {
  w.x[id] = x;
  w.y[id] = y;
  w.aim[id] = 0;
  w.health[id] = MAX_HEALTH;
  w.ammo[id] = CLIP_SIZE;
  w.reserve[id] = RESERVE_START;
  w.cooldown[id] = 0;
  w.reload[id] = 0;
  w.invuln[id] = SPAWN_PROTECT_TICKS;
  w.shotFired[id] = 0;
  w.weapon[id] = WEAPON_RIFLE;
  w.switchTicks[id] = 0;
  w.grenades[id] = GRENADE_START;
  w.throwCooldown[id] = 0;
  w.switchHeld[id] = 0;
  w.grenadeThrown[id] = 0;
  w.camoTicks[id] = 0; // the charge does not survive death; the prop choice does
  w.revealTicks[id] = 0;
  w.disguised[id] = 0;
}

export function removePlayer(w: World, id: number): void {
  w.alive[id] = 0;
  // Bullets in flight from a departed player keep flying; their owner slot
  // may be reused, which only affects who gets credit. Acceptable for v1.
}

export function isDead(w: World, id: number): boolean {
  return w.health[id] === 0;
}

// Deterministic noise in [-1, 1] from the input sequence and player id, so
// client prediction and the server pick the same spread for each shot.
export function shotNoise(seq: number, id: number): number {
  let h = (Math.imul(seq, 374761393) + Math.imul(id + 1, 668265263)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return ((h >>> 0) / 4294967296) * 2 - 1;
}

// Tiny inset so a body resting exactly on a tile edge does not count as
// overlapping the next tile over.
const EDGE_EPS = 0.001;

// Pure function of (state, input, dt). Used identically on the server (one
// call per consumed input) and on the client (prediction + replay).
// `throwDist`: the aimed throw distance in 2 px units (u8 on the wire),
// only read with the grenade out.
export function applyInput(
  w: World,
  id: number,
  seq: number,
  buttons: number,
  aim: number,
  dt: number,
  throwDist = 0,
): void {
  w.shotFired[id] = 0;
  w.grenadeThrown[id] = 0;
  w.aim[id] = aim;
  w.buttons[id] = buttons;
  w.lastSeq[id] = seq;
  if (w.invuln[id]! > 0) w.invuln[id] = w.invuln[id]! - 1;
  if (w.camoTicks[id]! > 0) w.camoTicks[id] = w.camoTicks[id]! - 1;
  if (w.revealTicks[id]! > 0) w.revealTicks[id] = w.revealTicks[id]! - 1;

  // The dead don't move or shoot; they wait for the server to respawn them.
  if (w.health[id] === 0) {
    w.fireHeld[id] = buttons & BTN_FIRE ? 1 : 0;
    w.switchHeld[id] = buttons & BTN_SWITCH ? 1 : 0;
    w.disguised[id] = 0;
    return;
  }

  // W/S are forward/back, A/D strafe. In aim-relative mode these are rotated
  // by `aim`; in screen-relative mode they map straight onto the screen axes.
  let forward = 0;
  let strafe = 0;
  if (buttons & BTN_UP) forward += 1;
  if (buttons & BTN_DOWN) forward -= 1;
  if (buttons & BTN_RIGHT) strafe += 1;
  if (buttons & BTN_LEFT) strafe -= 1;
  const moving = forward !== 0 || strafe !== 0;

  if (forward !== 0 && strafe !== 0) {
    // Normalise diagonals. 1/sqrt(2); a literal to stay deterministic.
    forward *= 0.70710678;
    strafe *= 0.70710678;
  }

  const cos = Math.cos(aim);
  const sin = Math.sin(aim);
  let dx: number;
  let dy: number;
  if (buttons & FLAG_SCREEN_RELATIVE) {
    // Screen-relative: W is up (-y), D is right (+x), regardless of aim.
    dx = strafe;
    dy = -forward;
  } else {
    // Aim-relative. Screen space has y down, so rotation 0 faces +x and the
    // player's right hand side is +90 degrees clockwise, i.e. (-sin, cos).
    dx = forward * cos - strafe * sin;
    dy = forward * sin + strafe * cos;
  }

  // Backpedal penalty: scale by how much of the movement points away from
  // the aim. dot is in [-1, 1] because both vectors are unit length.
  // Running is the default; WALK held = slower, steadier.
  const walking = (buttons & BTN_WALK) !== 0;
  const running = moving && !walking;
  let speed = (running ? RUN_SPEED : WALK_SPEED) * w.level.speedScale;
  const dot = dx * cos + dy * sin;
  if (dot < 0) speed *= 1 + (1 - BACKPEDAL_SPEED_FACTOR) * dot;

  moveAndCollide(w, id, dx * speed * dt, dy * speed * dt);

  // Weapon switch on the press edge; the hands are busy for WEAPON_SWITCH_TICKS.
  const switchDown = (buttons & BTN_SWITCH) !== 0;
  if (switchDown && w.switchHeld[id] === 0) {
    w.weapon[id] = w.weapon[id] === WEAPON_RIFLE ? WEAPON_GRENADE : WEAPON_RIFLE;
    w.switchTicks[id] = WEAPON_SWITCH_TICKS;
  }
  w.switchHeld[id] = switchDown ? 1 : 0;
  if (w.switchTicks[id]! > 0) w.switchTicks[id] = w.switchTicks[id]! - 1;
  if (w.throwCooldown[id]! > 0) w.throwCooldown[id] = w.throwCooldown[id]! - 1;
  const handsFree = w.switchTicks[id] === 0;
  // One press edge for both weapons (one shot or one throw per click).
  const fireDown = (buttons & BTN_FIRE) !== 0;
  const firePressed = fireDown && w.fireHeld[id] === 0;
  w.fireHeld[id] = fireDown ? 1 : 0;
  // The rifle keeps cooling down and reloading on the back; it only fires
  // or starts a reload when it is the weapon in hand.
  updateWeapon(w, id, seq, buttons, aim, moving, running, handsFree && w.weapon[id] === WEAPON_RIFLE, firePressed);
  if (handsFree && w.weapon[id] === WEAPON_GRENADE) throwGrenade(w, id, firePressed, aim, throwDist);

  // Camouflage: a prop only while charged, still, and not just having shot.
  if (w.shotFired[id] === 1 || w.grenadeThrown[id] === 1) w.revealTicks[id] = CAMO_REVEAL_TICKS;
  w.disguised[id] =
    w.camoTicks[id]! > 0 && !moving && w.revealTicks[id] === 0 ? w.camoProp[id]! : 0;
}

// Rifle bookkeeping: cooldown, reload, one bullet per fire press.
function updateWeapon(
  w: World,
  id: number,
  seq: number,
  buttons: number,
  aim: number,
  moving: boolean,
  running: boolean,
  inHand: boolean,
  pressed: boolean,
): void {
  const firePressed = pressed && inHand;

  if (w.cooldown[id]! > 0) w.cooldown[id] = w.cooldown[id]! - 1;

  if (w.reload[id]! > 0) {
    w.reload[id] = w.reload[id]! - 1;
    if (w.reload[id] === 0) {
      const want = CLIP_SIZE - w.ammo[id]!;
      const take = want < w.reserve[id]! ? want : w.reserve[id]!;
      w.ammo[id] = w.ammo[id]! + take;
      w.reserve[id] = w.reserve[id]! - take;
    }
    return; // can't fire while reloading
  }

  if (inHand && buttons & BTN_RELOAD && w.ammo[id]! < CLIP_SIZE && w.reserve[id]! > 0) {
    w.reload[id] = RELOAD_TICKS;
    return;
  }

  if (firePressed && w.cooldown[id] === 0) {
    if (w.ammo[id]! > 0) {
      w.ammo[id] = w.ammo[id]! - 1;
      w.cooldown[id] = RIFLE_COOLDOWN_TICKS;
      w.shotFired[id] = 1;
      if (!w.out.suppressSpawns) {
        const spread = running ? SPREAD_RUN : moving ? SPREAD_WALK : SPREAD_STAND;
        const angle = aim + shotNoise(seq, id) * spread;
        const ox = w.x[id]! + Math.cos(angle) * (PLAYER_RADIUS + 6);
        const oy = w.y[id]! + Math.sin(angle) * (PLAYER_RADIUS + 6);
        spawnProjectile(w, id, ox, oy, angle);
      }
    } else if (w.reserve[id]! > 0) {
      w.reload[id] = RELOAD_TICKS; // dry fire starts a reload
    }
  }
}

// Grenade in hand: one throw per fire press, GRENADE_COOLDOWN_TICKS apart,
// at the aimed distance clamped to the range. The count and cooldown are
// spent even during a prediction replay (suppressSpawns); only the
// projectile itself is skipped then.
function throwGrenade(w: World, id: number, pressed: boolean, aim: number, throwDist: number): void {
  if (!pressed || w.throwCooldown[id]! > 0 || w.grenades[id] === 0) return;
  w.grenades[id] = w.grenades[id]! - 1;
  w.throwCooldown[id] = GRENADE_COOLDOWN_TICKS;
  w.grenadeThrown[id] = 1;
  if (w.out.suppressSpawns) return;
  let dist = throwDist * 2;
  if (dist < GRENADE_RANGE_MIN) dist = GRENADE_RANGE_MIN;
  if (dist > GRENADE_RANGE_MAX) dist = GRENADE_RANGE_MAX;
  spawnGrenade(w, id, w.x[id]!, w.y[id]!, aim, dist);
}

// Axis-separated sweep against the solid tile grid: move on X and push out
// of any wall, then the same on Y. Separating the axes is what gives free
// sliding along walls. The body is treated as an axis-aligned box of
// half-size PLAYER_RADIUS; a per-tick step is always smaller than a tile
// (RUN_SPEED / TICK_RATE = 5 px vs 32 px) so checking only the leading edge
// column/row is enough.
function moveAndCollide(w: World, id: number, mx: number, my: number): void {
  const level = w.level;
  const ts = level.tileSize;
  const r = PLAYER_RADIUS;
  const minX = r;
  const minY = r;
  const maxX = level.widthPx - r;
  const maxY = level.heightPx - r;

  let x = w.x[id]!;
  let y = w.y[id]!;

  // ---- X axis
  if (mx !== 0) {
    x += mx;
    if (x < minX) x = minX;
    else if (x > maxX) x = maxX;

    const rowTop = ((y - r + EDGE_EPS) / ts) | 0;
    const rowBottom = ((y + r - EDGE_EPS) / ts) | 0;
    if (mx > 0) {
      const col = ((x + r - EDGE_EPS) / ts) | 0;
      for (let row = rowTop; row <= rowBottom; row++) {
        if (isSolid(level, col, row)) {
          x = col * ts - r;
          break;
        }
      }
    } else {
      const col = ((x - r + EDGE_EPS) / ts) | 0;
      for (let row = rowTop; row <= rowBottom; row++) {
        if (isSolid(level, col, row)) {
          x = (col + 1) * ts + r;
          break;
        }
      }
    }
  }

  // ---- Y axis
  if (my !== 0) {
    y += my;
    if (y < minY) y = minY;
    else if (y > maxY) y = maxY;

    const colLeft = ((x - r + EDGE_EPS) / ts) | 0;
    const colRight = ((x + r - EDGE_EPS) / ts) | 0;
    if (my > 0) {
      const row = ((y + r - EDGE_EPS) / ts) | 0;
      for (let col = colLeft; col <= colRight; col++) {
        if (isSolid(level, col, row)) {
          y = row * ts - r;
          break;
        }
      }
    } else {
      const row = ((y - r + EDGE_EPS) / ts) | 0;
      for (let col = colLeft; col <= colRight; col++) {
        if (isSolid(level, col, row)) {
          y = (row + 1) * ts + r;
          break;
        }
      }
    }
  }

  w.x[id] = x;
  w.y[id] = y;
}
